import { timezoneAliases, timeZoneMap } from "./resource";

// Sample timezone list - replace with your actual list
export const timezoneList = timeZoneMap.map(({ label, zone }) => ({
  label,
  value: zone,
}));

// Normalize timezone using aliases
export const normalizeTimezone = (timezone) => {
  return timezoneAliases[timezone] || timezone;
};

// Get UTC offset for a specific date and timezone
const getUTCOffset = (date, timezone) => {
  try {
    const utc = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    const local = new Date(
      date.toLocaleString("en-US", { timeZone: timezone })
    );
    return (local.getTime() - utc.getTime()) / (1000 * 60);
  } catch (e) {
    return null;
  }
};

// Get timezone information including DST behavior
export const getTimezoneInfo = (timezone) => {
  const now = new Date();
  const jan = new Date(now.getFullYear(), 0, 1);
  const jul = new Date(now.getFullYear(), 6, 1);

  const janOffset = getUTCOffset(jan, timezone);
  const julOffset = getUTCOffset(jul, timezone);

  if (janOffset === null || julOffset === null) return null;

  return {
    timezone,
    winterOffset: janOffset,
    summerOffset: julOffset,
    usesDST: janOffset !== julOffset,
  };
};

// Find timezone in list using multiple strategies
export const findTimezoneInList = (targetTimezone) => {
  // Strategy 1: Direct match after normalization
  const normalizedTarget = normalizeTimezone(targetTimezone);
  let found = timezoneList.find((tz) => tz.value === normalizedTarget);
  if (found)
    return {
      label: `${found.label} (${formatTimeInZone(found.value)})`,
      zone: found.value,
    };

  // Strategy 2: Check if any timezone in list normalizes to the same value
  found = timezoneList.find(
    (tz) => normalizeTimezone(tz.value) === normalizedTarget
  );
  if (found)
    return {
      label: `${found.label} (${formatTimeInZone(found.value)})`,
      zone: found.value,
    };

  // Strategy 3: UTC offset matching
  const targetInfo = getTimezoneInfo(targetTimezone);
  if (targetInfo) {
    found = timezoneList.find((tz) => {
      const tzInfo = getTimezoneInfo(tz.value);
      return (
        tzInfo &&
        tzInfo.winterOffset === targetInfo.winterOffset &&
        tzInfo.summerOffset === targetInfo.summerOffset &&
        tzInfo.usesDST === targetInfo.usesDST
      );
    });
    if (found)
      return {
        label: `${found.label} (${formatTimeInZone(found.value)})`,
        zone: found.value,
      };
  }

  const { label, value } = timezoneList.find(
    (tz) => tz.value.toLowerCase() === targetTimezone.toLowerCase()
  );

  // Strategy 4: Case-insensitive fallback
  return { labe: `${label} (${formatTimeInZone(value)})`, zone: value };
};

export const formatTimeInZone = (zone) => {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date());
};

export const systemTimeZone = () => {
  const userZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { label, zone } = findTimezoneInList(userZone);

  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "shortOffset",
  });

  const parts = formatter.formatToParts(now);
  const offsetPart = parts.find((p) => p.type === "timeZoneName")?.value || "";
  const timePart = parts
    .filter((p) => ["hour", "minute", "dayPeriod", "literal"].includes(p.type))
    .map((p) => p.value)
    .join("");

  // Replace 'GMT+05:30' with 'UTC +05:30'
  const offset = offsetPart
    .replace("GMT", "UTC ")
    .replace("UTC+", "UTC +")
    .replace("UTC-", "UTC -");

  const initialSelected = {
    label: `${offset}, ${label}`,
    zone,
  };

  return initialSelected;
};

export const viewerTimeZone = () => {
  const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return viewerZone;
};
